<script setup lang="ts">
import axios from 'axios'
import { orderBy, round } from 'lodash-es'
import { Link, useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useHelpers } from '@/Composables/helpers'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'
import CheckoutLayout from '@/Layouts/CheckoutLayout.vue'
import { useCartStore } from '@/Stores/cart.store'
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import FormGroup from '@/Components/Forms/FormGroup.vue'
import Input from '@/Components/Forms/Input.vue'
import Select from '@/Components/Forms/Select.vue'
import { Country } from '@interfaces/country.interface'
import { watch, ref, onMounted, computed, nextTick, inject } from 'vue'
import { ShippingMethod, ShippingMethodOption } from '@interfaces/shipping-method.interface'
import PhoneNumber from '@/Components/Forms/PhoneNumber.vue'
import Button from '@/Components/Button.vue'
import { ShoppingBagIcon } from '@heroicons/vue/24/outline'
import type ziggyRoute from 'ziggy-js'
import Card from '@/Components/Card.vue'
import CardTitle from '@/Components/CardTitle.vue'
import Steps from './Partials/Steps.vue'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
import CouponForm from './Partials/CouponForm.vue'
import LoadingSpinnerIcon from '@/Components/Icons/LoadingSpinnerIcon.vue'
import LockerCombobox from './Partials/LockerCombobox.vue'

defineOptions({ layout: CheckoutLayout })

const route = inject<typeof ziggyRoute>('route')

const props = defineProps<{
    countries: Array<Country>,
}>()

const root = ref<HTMLElement | null>(null)

const { t, n } = useI18n()
const { asset } = useHelpers()
const page = usePage()
const cart = useCartStore()

const defaultCountry = () => {
    // Select country by current region
    const regionCountry = props.countries.find(({ alpha_2 }) => alpha_2 === page.props.app.localization.region_code)

    if (regionCountry) {
        return regionCountry.id
    }

    // Fallback to first country
    return props.countries[0].id
}

const country = ref<Country | null>(null)
const form = useForm<{
    billing_first_name: string,
    billing_last_name: string,
    billing_email: string,
    shipping_country_id: number,
    shipping_address: string,
    shipping_city: string,
    shipping_postcode: string,
    shipping_phone_number: string,
    shipping_method_id: number | string,
    shipping_method_option_id: ShippingMethodOption | null,
}>({
    billing_first_name: '',
    billing_last_name: '',
    billing_email: '',
    shipping_country_id: defaultCountry(),
    shipping_address: '',
    shipping_city: '',
    shipping_postcode: '',
    shipping_phone_number: '',
    shipping_method_id: '',
    shipping_method_option_id: null,
})

const updateCountry = () => {
    axios.get(`/api/countries/${form.shipping_country_id}`)
        .then((response) => {
            country.value = response.data.data

            // Set default shipping method
            form.shipping_method_id = orderBy(country.value!.shipping_methods, ['is_free_shipping', 'price'], ['desc', 'asc'])[0].id
        })
}

const shippingMethodPrice = (shippingMethod: ShippingMethod) => {
    // const total = cart.subtotal - this.cartDiscount
    const total = cart.subtotal

    if (country.value && shippingMethod.is_free_shipping && total >= country.value.free_shipping_threshold) {
        const price = shippingMethod.price! - country.value.free_shipping_amount

        return price >= 0 ? price : 0
    }

    if (shippingMethod.free_shipping_threshold !== null && total >= shippingMethod.free_shipping_threshold!) {
        return 0
    }

    return shippingMethod.price ?? 0
}

const shippingMethod = computed(() => {
    if (!country.value) {
        return null
    }

    return country.value.shipping_methods?.find(({ id }) => id === form.shipping_method_id) || null
})

const shipping = computed(() => {
    if (shippingMethod.value) {
        return shippingMethodPrice(shippingMethod.value)
    }

    return 0
})

const vat = computed(() => {
    return country.value?.vat || cart.defaultVat || 0
})

const tax = computed(() => {
    return round(total.value - (total.value / (1 + (vat.value / 100))), 2)
})

const total = computed(() => {
    return cart.subtotal + shipping.value - cart.discount
})

watch(() => form.shipping_country_id, () => {
    form.shipping_method_id = ''
    form.shipping_method_option_id = null

    updateCountry()
})

watch(() => form.shipping_method_id, () => {
    form.shipping_method_option_id = null
})

onMounted(() => {
    updateCountry()

    if (page.props.user) {
        form.billing_first_name = page.props.user.first_name
        form.billing_last_name = page.props.user.last_name
        form.billing_email = page.props.user.email
        form.shipping_phone_number = page.props.user.phone_number
    }
})

const checkout = async () => {
    form.transform(data => {
        const body: any = {
            billing_first_name: data.billing_first_name,
            billing_last_name: data.billing_last_name,
            billing_email: data.billing_email,
            shipping_country_id: data.shipping_country_id,
            shipping_phone_number: data.shipping_phone_number,
            shipping_method_id: data.shipping_method_id,
            // shipping_comment: this.form.shipping.comment,
        }

        if (shippingMethod.value?.is_pickup_point) {
            body.shipping_method_option_id = data.shipping_method_option_id?.id
        }

        if (!shippingMethod.value?.is_pickup_point) {
            body.shipping_address = data.shipping_address
            body.shipping_city = data.shipping_city
            body.shipping_postcode = data.shipping_postcode
        }

        return body
    }).post(route!('checkout'), {
        preserveScroll: (page) => Object.keys(page.props.errors).length > 0,
        onError: () => {
            nextTick(() => {
                const errorsEl: HTMLElement | null = root.value?.querySelector('.field-error') || null

                if (errorsEl) {
                    errorsEl.scrollIntoView({
                        block: "center",
                        behavior: "smooth",
                    })
                }
            })
        },
    })
}
</script>

<template>
    <div>
        <ApplicationHead :title="t('pages.checkout.title')">
            <meta property="og:type" content="website">
            <meta property="og:image" :content="asset('/images/social-logo.png')">

            <meta name="robots" content="noindex,follow">
        </ApplicationHead>

        <div v-if="cart.itemsLoading" class="fixed flex items-center justify-center w-full h-full text-grayscale-600">
            <LoadingSpinnerIcon class="w-5 h-5 mr-3 animate-spin" />
            <span>{{ $t('components.button.loading') }}...</span>
        </div>

        <div v-else-if="cart.items.length === 0" class="fixed flex items-center justify-center w-full h-full bg-white">
            <div class="my-auto text-center">
                <div class="flex items-center justify-center rounded-full bg-grayscale-50 w-[86px] h-[86px] mx-auto">
                    <ShoppingBagIcon class="w-12 h-12 mx-auto stroke-1 text-grayscale-900" aria-hidden="true" />
                </div>

                <p class="mt-8 text-p1 text-grayscale-700">{{ $t('components.cart.cart-empty') }}</p>

                <Button as="link" :href="route('home')" class="mt-12" size="md" color="primary">
                    {{ $t('components.cart.continue-shopping') }}
                </Button>
            </div>
        </div>

        <template v-else>
            <div class="max-w-xl px-4 mx-auto lg:max-w-6xl sm:px-6 lg:px-8">
                <div class="flex mt-4 sm:mt-6">
                    <Link :href="route('home')" class="flex items-center font-medium text-p2 text-grayscale-700 hover:text-grayscale-800">
                    <ChevronLeftIcon class="w-4 h-4 mr-1" />
                    {{ $t('components.cart.continue-shopping') }}
                    </Link>
                </div>
            </div>

            <div ref="root" class="relative max-w-xl px-4 pb-6 mx-auto lg:max-w-6xl sm:px-6 lg:px-8">
                <div class="mt-8 sm:mt-[68px]">
                    <h1 class="font-bold text-h5 text-grayscale-900">{{ t('pages.checkout.title') }}</h1>
                    <Steps :level="0" class="mt-6" />
                </div>

                <div class="grid grid-cols-1 mt-4 gap-x-10 lg:grid-cols-2">
                    <div class="pb-6 lg:col-start-2 lg:row-start-1 lg:pb-0">
                        <Card as="section" aria-labelledby="summary-heading">
                            <template #header>
                                <CardTitle id="summary-heading" as="h2">{{ t('pages.checkout.order-summary') }}</CardTitle>
                            </template>

                            <CouponForm class="mb-6" />

                            <ul role="list" class="space-y-6 text-p2 text-grayscale-900">
                                <li v-for="item in cart.items" :key="item.id" class="flex items-start space-x-2 sm:space-x-5">
                                    <div class="relative flex-none overflow-hidden rounded-lg w-20 h-20 sm:w-[110px] sm:h-[110px]">
                                        <img :src="item.product.image_url" :alt="item.product.title" class="object-cover object-center w-full h-full" />
                                        <div class="absolute inset-0 bg-black bg-opacity-[0.03] transition-colors"></div>
                                    </div>

                                    <div class="flex flex-col flex-auto min-h-[5rem] sm:min-h-[110px] font-medium text-p2">
                                        <p class="flex-1">{{ item.product.title }}</p>

                                        <div class="flex-shrink-0 mt-1">
                                            <p v-if="item.product.variant" class="font-normal text-grayscale-700">{{ item.product.variant }}</p>
                                            <p class="font-normal text-grayscale-700">{{ t('components.cart.quantity') }}: <span class="text-grayscale-800">1</span></p>
                                        </div>
                                    </div>

                                    <div class="flex-none font-medium text-p1">
                                        <p class="whitespace-nowrap" :class="{ 'line-through text-grayscale-100 decoration-grayscale-800': item.product.sale_price }">{{ n(item.product.retail_price, 'currency') }}</p>
                                        <p v-if="item.product.sale_price" class="text-error-600 whitespace-nowrap">{{ n(item.product.sale_price, 'currency') }}</p>
                                    </div>
                                </li>
                            </ul>

                            <dl class="pt-8 space-y-4 text-p2 text-grayscale-900 lg:block">
                                <div class="flex items-center justify-between">
                                    <dt>{{ t('components.cart.subtotal') }}</dt>
                                    <dd class="font-medium">{{ n(cart.subtotal, 'currency') }}</dd>
                                </div>

                                <div v-for="coupon in cart.meta?.coupons" :key="coupon.coupon_id" class="flex items-center justify-between">
                                    <dt>
                                        {{ t('pages.checkout.discount') }}
                                        <span class="ml-2 rounded-full bg-mint-100 px-2 py-0.5 text-p3 tracking-wide text-mint-700">{{ coupon.coupon_code.toUpperCase() }}</span>
                                    </dt>
                                    <dd class="font-medium">{{ n(coupon.discount * -1, 'currency') }}</dd>
                                </div>

                                <div class="flex items-center justify-between">
                                    <dt>{{ t('pages.checkout.shipping') }}</dt>
                                    <dd class="font-medium">{{ n(shipping, 'currency') }}</dd>
                                </div>

                                <div class="flex items-center justify-between">
                                    <dt>{{ t('components.cart.vat') }} {{ vat }}%</dt>
                                    <dd class="font-medium">{{ n(tax, 'currency') }}</dd>
                                </div>

                                <div class="flex items-center justify-between pt-4 font-medium border-t border-grayscale-100 text-p1">
                                    <dt>{{ t('components.cart.total') }}</dt>
                                    <dd>{{ n(total, 'currency') }}</dd>
                                </div>
                            </dl>
                        </Card>
                    </div>

                    <form class="lg:col-start-1 lg:row-start-1">
                        <Card as="section" aria-labelledby="billing-details-heading">
                            <template #header>
                                <CardTitle id="billing-details-heading" as="h2">{{ t('pages.checkout.billing-details') }}</CardTitle>
                            </template>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                                <FormGroup name="billing_first_name" :label="t('pages.checkout.first-name')" :error="form.errors.billing_first_name">
                                    <Input id="billing_first_name" v-model="form.billing_first_name" type="text" class="w-full" />
                                </FormGroup>

                                <FormGroup name="billing_last_name" :label="t('pages.checkout.last-name')" :error="form.errors.billing_last_name">
                                    <Input id="billing_last_name" v-model="form.billing_last_name" type="text" class="w-full" />
                                </FormGroup>

                                <FormGroup class="sm:col-span-2" name="billing_email" :label="t('pages.checkout.email-address')" :error="form.errors.billing_email">
                                    <Input id="billing_email" v-model="form.billing_email" type="text" class="w-full" />
                                </FormGroup>

                                <FormGroup class="sm:col-span-2" name="billing_country_id" :label="t('pages.checkout.country')" :error="form.errors.shipping_country_id">
                                    <Select id="billing_country_id" v-model="form.shipping_country_id" class="w-full">
                                        <option v-for="({ id, name }) in countries" :key="id" :value="id">{{ name }}</option>
                                    </Select>
                                </FormGroup>
                            </div>
                        </Card>

                        <template v-if="country">
                            <Card as="section" aria-labelledby="shipping-heading" class="mt-6 lg:mt-10">
                                <template #header>
                                    <CardTitle id="shipping-heading" as="h2">{{ t('pages.checkout.shipping') }}</CardTitle>
                                </template>

                                <RadioGroup v-model="form.shipping_method_id" class="mt-6">
                                    <div class="-my-4 divide-y divide-grayscale-100">
                                        <RadioGroupOption v-for="method in country.shipping_methods" :key="method.name" v-slot="{ checked, active }" as="template" :value="method.id">
                                            <div class="relative flex items-start py-4">
                                                <span :class="[checked ? 'bg-primary-600 border-transparent' : 'bg-white border-grayscale-200', active ? 'ring-2 ring-offset-2 ring-primary-600' : '', 'mt-[3px] h-4 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center']" aria-hidden="true">
                                                    <span class="rounded-full bg-white w-1.5 h-1.5" />
                                                </span>

                                                <span class="flex-1 ml-3">
                                                    <RadioGroupLabel as="span" class="flex w-full text-p2 text-grayscale-700">
                                                        <span class="flex-1 font-medium">{{ method.name === 'DPD Private' ? t('pages.checkout.shipping-methods.dpd-private') : method.name }}</span>
                                                        <span v-if="method.free_shipping_threshold" class="ml-2 -my-1 rounded bg-mint-50 px-3 py-1.5 text-p3 font-medium text-grayscale-700">{{ t('pages.checkout.free-from') }} <span class="text-grayscale-800">{{ $n(method.free_shipping_threshold, 'currency', { minimumFractionDigits: 0 }) }}</span></span>
                                                    </RadioGroupLabel>
                                                    <RadioGroupDescription as="span" class="text-grayscale-500 font-medium block text-p2 mt-0.5">{{ n(shippingMethodPrice(method), 'currency') }}</RadioGroupDescription>
                                                </span>
                                            </div>
                                        </RadioGroupOption>
                                    </div>
                                </RadioGroup>

                                <div v-if="shippingMethod" class="grid grid-cols-1 mt-6 sm:grid-cols-2 gap-x-4 gap-y-6">
                                    <template v-if="shippingMethod?.is_pickup_point && shippingMethod?.options?.length">
                                        <FormGroup name="shipping_method_option_id" class="sm:col-span-2" :label="t('pages.checkout.select-location')" :error="form.errors.shipping_method_option_id">
                                            <LockerCombobox v-model="form.shipping_method_option_id" :options="shippingMethod?.options || []" />
                                        </FormGroup>
                                    </template>

                                    <template v-else>
                                        <FormGroup name="shipping_address" class="sm:col-span-2" :label="t('pages.checkout.address')" :error="form.errors.shipping_address">
                                            <Input id="shipping_address" v-model="form.shipping_address" type="text" class="w-full" />
                                        </FormGroup>

                                        <FormGroup name="shipping_city" :label="t('pages.checkout.city')" :error="form.errors.shipping_city">
                                            <Input id="shipping_city" v-model="form.shipping_city" type="text" class="w-full" />
                                        </FormGroup>

                                        <FormGroup name="shipping_postcode" :label="t('pages.checkout.postcode')" :error="form.errors.shipping_postcode">
                                            <Input id="shipping_postcode" v-model="form.shipping_postcode" type="text" class="w-full" />
                                        </FormGroup>
                                    </template>

                                    <FormGroup name="shipping_phone_number" class="sm:col-span-2" :label="t('pages.checkout.phone-number')" :error="form.errors.shipping_phone_number">
                                        <PhoneNumber id="shipping_phone_number" v-model="form.shipping_phone_number" class="w-full" :initial-country="country.alpha_2" />
                                    </FormGroup>
                                </div>
                            </Card>
                        </template>

                        <div class="mt-6">
                            <p class="text-p3 text-grayscale-700">{{ t('pages.checkout.privacy-message') }}</p>
                            <Button type="button" size="lg" color="primary" class="w-full mt-6" :loading="form.processing" @click="checkout">
                                {{ t('pages.checkout.place-order') }}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </template>
    </div>
</template>
